import { FakedCodelists } from '@administration/codelists/codelists.interface';
import { Codelists } from '@administration/codelists/codelists.service';
import { inject, Injector, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { QueryService } from '@common/services/query.service';
import { mapResultData, takeUntilResultFinalize } from '@ngneat/query';
import { map, of, Subscription } from 'rxjs';

@Pipe({
    name: 'codelist'
})
export class CodelistPipe implements PipeTransform, OnDestroy {
    private queryService = inject(QueryService);

    private codelistSubscription: Subscription = new Subscription();
    injector = inject(Injector);

    ngOnDestroy(): void {
        this.codelistSubscription.unsubscribe();
    }

    /**
     * Transforms a given codelist item ID to its corresponding display text.
     *
     * @param id The id of the codelist item to be transformed.
     * @param codelistName The name of the codelist from which to fetch the display value.
     * @param prefixItemWithId (Optional) Whether to prefix the display value with the item's code or ID. Defaults to true.
     * @returns An observable that emits the transformed display text, or a default value in case of error or if not found.
     *
     * @example
     * {{ row[column.field] | codelist: column.codelistName : false | async }}
     */
    transform(id: string | number, codelistName: Codelists | FakedCodelists, prefixItemWithId: boolean = false) {
        if (!codelistName || typeof codelistName !== 'string') return of(null);
        if (!id) return of('-');

        const codelistQuery$ = this.queryService.getCodelistQuery<string>({
            name: codelistName,
            selectedIds: [id],
            take: 1,
            filter: null,
            injector: this.injector
        }).result$;

        return codelistQuery$.pipe(
            mapResultData((data) => this.getText(data?.[0], id, codelistName, prefixItemWithId)),
            takeUntilResultFinalize(),
            map((result) => result.data)
        );
    }

    private getText(item: any, id: string | number, codelist: string, prefixItemWithId: boolean): string {
        if (!item) {
            console.error(`${id} not found in codelist ${codelist}`);
            return '-';
        }

        const itemPrefix = prefixItemWithId ? `${item.code || item.id} - ` : '';
        return item.customText || `${itemPrefix}${item.name}`;
    }
}
